/**
 * ConfigureStep — the add-account page for a type Lookout creates locally (ACT2-1031: the type
 * picker lands here directly). Renders, top to bottom:
 *   1. the account identity — type-or-pick fields whose suggestions are the synced-destination
 *      buckets (`AccountIdentityFields`); this replaced the old step 2,
 *   2. a short highlight Callout with the type's setup note (if any),
 *   3. a success/error Callout with the connection-test result, plus a warning Callout when the
 *      test passed but the account cannot write Reverse ETL's internal schema (ACT2-737),
 *   4. the type-specific form (a pure field collector), told to `hideIdentity`: the fields above are
 *      the one place the account is named, and the form still carries their values from its prefill.
 * Test/Save live in the drawer header, which drives the form through `formRef`.
 *
 * The form is prefilled from a representative destination in the selected bucket, falling back to
 * the bucket's own `(hostDim, dbDim)` when there is none (typed coordinates). The fallback is what
 * keeps the submitted config in agreement with the bucket the server re-derives from the same dims
 * (`assert<Type>ConfigMatchesBucketDims`). The form is keyed by the bucket, so a new identity
 * re-runs the prefill; the values typed so far ride across as a draft (`useDraftDefaults`), which
 * gives way wherever the new prefill moved a default, so the identity fields still take the new
 * dims. Test results are keyed by the bucket-derived connection key so the drawer
 * header reads the same entry.
 *
 * Redshift needs telling separately whether a destination supplied its prefill, because "a value is
 * present" is not the same question for its host and port — hence `manualEntry`.
 *
 * Snowflake with an existing role (ACT2-1116): the setup SQL grants that role USAGE on the database,
 * the compute warehouse and Lookout's compute warehouse, and nothing else (every holder of the role
 * can then spend those warehouses' credits). So a Test that fails with Snowflake's 2003 ("does not
 * exist or not authorized") or 090105 ("no current database", what a role with no USAGE on the
 * database gets, seen live) usually means the database name is wrong, or the grant was not run. One
 * that passes with zero tables means the role sees the database but has no USAGE on its schemas or
 * SELECT on its tables. 000606 ("No active warehouse selected") likewise means it cannot use the
 * warehouse. The form reports the role mode, and this step says so above the driver's own text.
 */
import { retlPrivilegeWarning } from '@server/retl-schema';
import type { Warehouse } from '@server/types';
import * as React from 'react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { WarehouseProblemCallout } from '@/_shared/components/WarehouseProblemCallout/WarehouseProblemCallout';

import type { SnowflakeRoleMode } from '../../../lib/snowflake-setup';
import {
  sameErrorCode,
  SNOWFLAKE_NO_ACTIVE_WAREHOUSE,
  SNOWFLAKE_NO_CURRENT_DATABASE,
  SNOWFLAKE_OBJECT_NOT_AUTHORIZED,
} from '../../../lib/warehouse-error-code';
import { warehouseSetupCallout } from '../../../lib/warehouse-setup-guides';
import { BigQueryForm } from '../bigquery/BigQueryForm';
import { DatabricksForm } from '../databricks/DatabricksForm';
import { PostgresForm } from '../postgres/PostgresForm';
import { RedshiftForm } from '../redshift/RedshiftForm';
import type { FormDraft, TestResult, WarehouseFormHandle } from '../shared/formHandle';
import { SnowflakeForm } from '../snowflake/SnowflakeForm';
import { retainedPromptMatches, type UseWarehouseAccountsReturn, type WarehouseBucket } from '../useWarehouseAccounts';
import type { WarehouseTypeMeta } from '../warehouseTypes';
import { AccountIdentityFields } from './AccountIdentityFields';

interface ConfigureStepProps {
  type: WarehouseTypeMeta;
  /** Representative destination in the bucket — prefill source (may be null). */
  warehouse: Warehouse | null;
  /** The selected bucket, or null until the identity fields name an account. */
  bucket: WarehouseBucket | null;
  /** Synced-destination buckets of this type — the identity fields' suggestions. */
  buckets?: WarehouseBucket[];
  /** Connection keys already stored, for the duplicate hint. */
  existingConnectionKeys?: string[];
  onSelectBucket?: (bucket: WarehouseBucket | null) => void;
  /** The drawer's sheet content, for the comboboxes' popups. */
  portalContainer?: React.RefObject<HTMLElement | null>;
  hook: UseWarehouseAccountsReturn;
  formRef: React.RefObject<WarehouseFormHandle | null>;
  /** Phase 3c decision 4: the admin reconnected to the account a deleted connection kept. */
  onReconnected?: () => void;
  /** Snowflake only: offer the customer's own private key and nothing else (see `SnowflakeForm`). */
  ownKeyOnly?: boolean;
  /** ACT2-1276: why the RudderStack permission blocks "Reconnect to the kept account", or null. */
  retainedReconnectBlocked?: string | null;
}

const NO_BUCKETS: WarehouseBucket[] = [];
const NO_KEYS: string[] = [];
const ignoreBucket = (): void => undefined;

const EMPTY_BUCKET: WarehouseBucket = {
  accountKey: '',
  type: '',
  hostDim: '',
  dbDim: '',
  availableWarehouses: [],
  destinationCount: 0,
  bucketWideConnected: false,
  schemaConnections: [],
  unmappedCount: 0,
};

/**
 * For the existing-role mode only (a role Lookout created has its grants from the setup SQL), else
 * null: "Role X can't see database <database>…" when Test fails with 2003/090105, "Role X can see
 * <database> but no tables in it…" when it passes with zero tables, and "Role X can't use the
 * warehouse…" on 000606. The error codes also come back for a database or warehouse that is mistyped
 * or was dropped, so those messages name both causes, and the caller keeps the driver's own text.
 */
function existingRoleAccessMessage(
  result: TestResult | undefined,
  role: { mode: SnowflakeRoleMode; name: string } | null,
  database: string,
): string | null {
  if (!result || role?.mode !== 'existing' || !role.name) return null;
  const name = role.name.toUpperCase();
  if (!result.success && sameErrorCode(result.errorCode, SNOWFLAKE_NO_ACTIVE_WAREHOUSE)) {
    return `Role ${name} can't use the warehouse. Check the warehouse name, or grant the role USAGE on it.`;
  }
  const db = database.toUpperCase();
  if (result.success) {
    // The setup SQL grants USAGE on the database, so a role with no schema or table grants passes.
    if (result.tableCount !== 0) return null;
    return `Role ${name} can see ${db || 'this database'} but no tables in it. Grant it USAGE on the schemas and SELECT on the tables.`;
  }
  const noAccess =
    sameErrorCode(result.errorCode, SNOWFLAKE_OBJECT_NOT_AUTHORIZED) ||
    sameErrorCode(result.errorCode, SNOWFLAKE_NO_CURRENT_DATABASE);
  if (!noAccess) return null;
  return `Role ${name} can't see ${db ? `database ${db}` : 'this database'}. Check the database name, or grant the role access to it.`;
}

/** Phrase a passing connection test, e.g. "Valid — ANALYTICS (42 tables)." */
function testSuccessMessage(result: TestResult): string {
  const tables =
    typeof result.tableCount === 'number'
      ? ` (${result.tableCount} ${result.tableCount === 1 ? 'table' : 'tables'})`
      : '';
  return result.database ? `Valid — ${result.database}${tables}.` : `Valid connection${tables}.`;
}

export function ConfigureStep({
  type,
  warehouse,
  bucket: selectedBucket,
  buckets = NO_BUCKETS,
  existingConnectionKeys = NO_KEYS,
  onSelectBucket = ignoreBucket,
  portalContainer,
  hook,
  formRef,
  onReconnected,
  ownKeyOnly = false,
  retainedReconnectBlocked = null,
}: ConfigureStepProps): React.JSX.Element {
  // Until the identity fields name an account the form renders against empty dims; the drawer keeps
  // Test disabled meanwhile.
  const bucket = selectedBucket ?? EMPTY_BUCKET;
  // Only the prompt raised for THIS type and bucket; a stale one from another add stays hidden.
  const retainedPrompt =
    selectedBucket && retainedPromptMatches(hook.retainedPrompt, type.key, bucket.hostDim, bucket.dbDim)
      ? hook.retainedPrompt
      : null;
  // The same rule for a refused Add (ACT2-1077): only this type and bucket's.
  const saveProblem =
    selectedBucket && retainedPromptMatches(hook.saveProblem, type.key, bucket.hostDim, bucket.dbDim)
      ? hook.saveProblem.problem
      : null;
  // '' when the bucket was typed by hand — every deriver then falls back to the bucket dims.
  const repId = warehouse?.rudderDestinationId ?? '';
  // Redshift alone needs telling: its prefill always yields a port, so presence cannot stand in
  // for "a destination supplied it". Every other form reads that off the value itself.
  const manualEntry = warehouse === null;
  // Snowflake's note depends on the auth method chosen inside the form, so the form reports it.
  const [snowflakeOwnKey, setSnowflakeOwnKey] = React.useState(ownKeyOnly);
  const [snowflakeRole, setSnowflakeRole] = React.useState<{ mode: SnowflakeRoleMode; name: string } | null>(null);
  const onSnowflakeRoleModeChange = React.useCallback(
    (mode: SnowflakeRoleMode, name: string) => setSnowflakeRole({ mode, name }),
    [],
  );
  const snowflakeExistingRole = type.key === 'SNOWFLAKE' && !snowflakeOwnKey && snowflakeRole?.mode === 'existing';
  const setupCallout = warehouseSetupCallout(type.key, { snowflakeOwnKey, snowflakeExistingRole });
  const testKey = hook.deriveTestKey(type.key, bucket.hostDim, bucket.dbDim);
  const result = selectedBucket ? hook.testResults[testKey] : undefined;
  const activationWarning = result?.retlSchema
    ? retlPrivilegeWarning(result.retlSchema, { optionalActivationGrants: snowflakeExistingRole })
    : null;
  const roleCannotRead =
    type.key === 'SNOWFLAKE' && !snowflakeOwnKey
      ? existingRoleAccessMessage(result, snowflakeRole, bucket.dbDim)
      : null;
  const busy = hook.isTesting || hook.isSaving;
  // Read off the form just before a new identity remounts it, so the typed values survive.
  const [draft, setDraft] = React.useState<FormDraft | undefined>(undefined);
  const selectBucket = (next: WarehouseBucket | null): void => {
    setDraft(formRef.current?.getDraft());
    onSelectBucket(next);
  };
  const availableWarehouses = bucket.availableWarehouses;

  // The identity commits on blur, so the click that leaves it for a form field remounts the form
  // before focus arrives, and the keystrokes that follow went nowhere. Remember the clicked field's
  // name and focus its remounted twin. (Tab is safe: it lands on the combobox's own toggle first.)
  const formArea = React.useRef<HTMLFieldSetElement>(null);
  const focusIntent = React.useRef<{ name: string; at: number } | null>(null);
  React.useEffect(() => {
    const intent = focusIntent.current;
    focusIntent.current = null;
    // Only the gesture that caused this remount, not one from long before it.
    if (intent === null || performance.now() - intent.at > 1000) return;
    // Focus with nowhere to go falls to the page or to the dialog's focus trap.
    const lost = (el: Element | null): boolean =>
      el === null || el === document.body || el.getAttribute('role') === 'dialog';
    const restore = (): void => {
      cleanup();
      if (!lost(document.activeElement)) return;
      formArea.current?.querySelector<HTMLElement>(`[name="${CSS.escape(intent.name)}"]`)?.focus();
    };
    // The moment the lost focus lands, so the next keystroke is not early; the timer covers a browser
    // that leaves it where it was.
    const onFocusIn = (e: FocusEvent): void => {
      // Once this focus event is done: moving focus from inside it is undone by the dialog's trap.
      if (lost(e.target as Element | null)) queueMicrotask(restore);
    };
    const timer = window.setTimeout(restore, 0);
    function cleanup(): void {
      window.clearTimeout(timer);
      document.removeEventListener('focusin', onFocusIn, true);
    }
    document.addEventListener('focusin', onFocusIn, true);
    return cleanup;
  }, [bucket.accountKey]);

  // Editing the form invalidates a prior test — clear the result so Save reverts to Test.
  const { resetTestResult } = hook;
  const onChange = React.useCallback(() => resetTestResult(testKey), [resetTestResult, testKey]);

  let form: React.JSX.Element;
  switch (type.key) {
    case 'SNOWFLAKE':
      form = (
        <SnowflakeForm
          ref={formRef}
          hideIdentity
          draft={draft}
          initialConfig={hook.deriveSnowflakePrefill(repId, bucket)}
          nameSeed={hook.deriveSnowflakeNameSeed(bucket)}
          onRoleModeChange={onSnowflakeRoleModeChange}
          availableWarehouses={availableWarehouses}
          portalContainer={portalContainer}
          ownKeyOnly={ownKeyOnly}
          onOwnKeyChange={setSnowflakeOwnKey}
          onChange={onChange}
          setupNote={setupCallout && <Callout variant="highlight">{setupCallout}</Callout>}
        />
      );
      break;
    case 'RS':
      form = (
        <RedshiftForm
          ref={formRef}
          hideIdentity
          draft={draft}
          initialConfig={hook.deriveRedshiftPrefill(repId, bucket)}
          manualEntry={manualEntry}
          onChange={onChange}
        />
      );
      break;
    case 'POSTGRES':
      form = (
        <PostgresForm
          ref={formRef}
          hideIdentity
          draft={draft}
          initialConfig={hook.derivePostgresPrefill(repId, bucket)}
          onChange={onChange}
        />
      );
      break;
    case 'DATABRICKS':
      form = (
        <DatabricksForm
          ref={formRef}
          hideIdentity
          draft={draft}
          initialConfig={hook.deriveDatabricksPrefill(repId, bucket)}
          availableWarehouses={availableWarehouses}
          portalContainer={portalContainer}
          onChange={onChange}
        />
      );
      break;
    case 'BQ':
      form = (
        <BigQueryForm
          ref={formRef}
          hideIdentity
          draft={draft}
          initialConfig={hook.deriveBigqueryPrefill(repId, bucket)}
          onChange={onChange}
        />
      );
      break;
    case 'CLICKHOUSE':
      // ClickHouse accounts use the platform account form, not this step.
      form = <p className="text-sm text-muted-foreground">Unsupported warehouse type.</p>;
  }

  // Only a server that predates ACT2-1077 sends a failed test without a `problem` (a rolling deploy).
  const legacyTestError = result && !result.success && !result.problem ? (result.error ?? null) : null;

  return (
    <div className="grid gap-4">
      <AccountIdentityFields
        type={type}
        buckets={buckets}
        bucket={selectedBucket}
        existingConnectionKeys={existingConnectionKeys}
        onSelect={selectBucket}
        portalContainer={portalContainer}
      />
      {retainedPrompt && (
        <Callout variant="info">
          <p>{retainedPrompt.message}</p>
          <div className="mt-2">
            <ActionTooltip show={retainedReconnectBlocked !== null} message={retainedReconnectBlocked ?? ''}>
              <Button
                type="button"
                variant="secondary"
                loading={hook.isReconnecting}
                disabled={retainedReconnectBlocked !== null}
                onClick={() => hook.reconnectRetained(onReconnected)}
                label="Reconnect to the kept account"
              />
            </ActionTooltip>
          </div>
        </Callout>
      )}
      {/* Snowflake shows its note inside the form, next to the authentication method it describes. */}
      {setupCallout && type.key !== 'SNOWFLAKE' && <Callout variant="highlight">{setupCallout}</Callout>}
      {result?.success && <Callout variant="success">{testSuccessMessage(result)}</Callout>}
      {/* An existing role that cannot read the database: beside a passing test (zero tables) it is a
          warning, since the connection works. On a failed test it is the problem's headline, with the
          server's next step and details kept, since a mistyped or dropped database gets the same codes. */}
      {roleCannotRead && result?.success && <Callout variant="warning">{roleCannotRead}</Callout>}
      {/* A passing test with no activation write access: the connection is usable and savable, so
          this is a warning next to the success, not in place of it. The copy comes from the backend
          module that owns the probe, so the two cannot disagree about what was checked. */}
      {result?.success && activationWarning && <Callout variant="warning">{activationWarning}</Callout>}
      {result && !result.success && result.problem && (
        <WarehouseProblemCallout problem={result.problem} {...(roleCannotRead ? { headline: roleCannotRead } : {})} />
      )}
      {/* During a rolling deploy this page can reach a server that predates ACT2-1077 and sends only
          its sentence; show that rather than nothing, under the role's own copy when there is one. */}
      {legacyTestError && (
        <Callout variant="error">
          {roleCannotRead ? (
            <>
              <p>{roleCannotRead}</p>
              <p className="mt-1 text-xs">{legacyTestError}</p>
            </>
          ) : (
            legacyTestError
          )}
        </Callout>
      )}
      {saveProblem && <WarehouseProblemCallout problem={saveProblem} />}
      {/* Disable all fields while a test/save is in flight. */}
      <fieldset
        key={bucket.accountKey || 'none'}
        ref={formArea}
        onPointerDownCapture={(e) => {
          const name = e.target instanceof Element ? e.target.closest('[name]')?.getAttribute('name') : null;
          if (name) focusIntent.current = { name, at: performance.now() };
        }}
        aria-label="Connection details"
        disabled={busy}
        className="grid min-w-0 gap-4"
      >
        {form}
      </fieldset>
    </div>
  );
}
